import type { ScoreboardApiGateway } from "@/modules/scores/scoreboard/gateway/scoreboardApiGateway"
import type { ScoreboardRankingDto, SessionScoreDto } from "@/modules/scores/scoreboard/gateway/scoreboardDto"
import {
  ScoreDifficultyEnum,
  ScoreType,
  type ExerciseScoreCategory,
  type ScoreDifficultyFilterEnum,
} from "@/modules/scores/scoreTypes"
import type { ExerciseCategory } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseTypes"
import type { PageableObject, PaginatedWrapper, SortObject } from "@/modules/utils/paginated"

const EMPTY_SORT: SortObject = {
  direction: "ASC",
  nullHandling: "NATIVE",
  ascending: true,
  property: "",
  ignoreCase: false,
}

const makePageable = (page: number, size: number): PageableObject => ({
  offset: page * size,
  sort: EMPTY_SORT,
  paged: true,
  pageNumber: page,
  pageSize: size,
  unpaged: false,
})

export const SCOREBOARD_TEST_DATA = {
  SESSION_SCORE: {
    DEFAULT: {
      user: {
        id: "user-1",
        username: "johndoe",
        firstname: "John",
        lastname: "Doe",
        avatar: null,
      },
      scores: [
        {
          score: {
            valueDecimal: 600,
            additionalValueDecimal: null,
            personalRecord: true,
            timeCapExceeded: false,
            difficulty: ScoreDifficultyEnum.RX,
            note: "Felt strong today!",
            noteAndMediaIsVisible: true,
            medias: null,
            reps: null,
            tieBreaks: null,
            scoreSetCompletions: null,
          },
          scoreType: ScoreType.TIME,
          exerciseId: "exercise-1",
          exerciseCategory: "WOD" as ExerciseCategory,
          otherCategory: null,
          orderIndex: 0,
        },
      ],
    } satisfies SessionScoreDto,
  },
}

export class FakeScoreboardApi implements ScoreboardApiGateway {
  private athleteScores = new Map<string, SessionScoreDto>()
  private exerciseScoreboards = new Map<string, ScoreboardRankingDto[]>()

  constructor() {
    // Default: any sessionId + athleteId combo returns the default session score
    this._defaultAthleteScore = structuredClone(SCOREBOARD_TEST_DATA.SESSION_SCORE.DEFAULT)
  }

  private _defaultAthleteScore: SessionScoreDto | null

  /* ── given* helpers ── */

  givenAthleteScoreboard(sessionId: string, athleteId: string, score: SessionScoreDto | null) {
    const key = `${sessionId}:${athleteId}`
    if (score === null) {
      this.athleteScores.delete(key)
    } else {
      this.athleteScores.set(key, structuredClone(score))
    }
  }

  givenExerciseScoreboard(exerciseId: string, rankings: ScoreboardRankingDto[]) {
    this.exerciseScoreboards.set(exerciseId, structuredClone(rankings))
  }

  givenNoAthleteScore() {
    this._defaultAthleteScore = null
  }

  /* ── ScoreboardApiGateway ── */

  async getAthleteScoreboard(sessionId: string, athleteId: string): Promise<SessionScoreDto | null> {
    const key = `${sessionId}:${athleteId}`
    const specific = this.athleteScores.get(key)
    return Promise.resolve(
      specific !== undefined ? structuredClone(specific) : structuredClone(this._defaultAthleteScore),
    )
  }

  async getExerciseScoreboard(
    _programId: string,
    _sessionId: string,
    exerciseId: string,
    _difficulty: ScoreDifficultyFilterEnum,
    _filter: ExerciseScoreCategory,
    page: number,
    size = 10,
  ): Promise<PaginatedWrapper<ScoreboardRankingDto>> {
    const rankings = this.exerciseScoreboards.get(exerciseId) ?? []
    const start = page * size
    const content = rankings.slice(start, start + size)

    return Promise.resolve({
      content,
      pageable: makePageable(page, size),
      totalElements: rankings.length,
      totalPages: Math.ceil(rankings.length / size) || 1,
      last: start + size >= rankings.length,
      size,
      number: page,
      first: page === 0,
      empty: content.length === 0,
      sort: EMPTY_SORT,
    })
  }

  async getMyExerciseScoreboardRanking(): Promise<ScoreboardRankingDto | null> {
    return Promise.resolve(null)
  }
}
